import React from 'react';
import {
  EDITORIAL_CORRECTION_PRESENTATIONS,
  resolveQuestionStatusPresentation,
  type PublicQuestionListItem as PublicQuestionListItemData,
} from '@find-my-part/types';
import { formatTimeAgo } from '../../lib/formatTimeAgo';
import { canonicalQuestionHref } from '../../lib/questionUrl';

export interface PublicQuestionListItemProps {
  question: PublicQuestionListItemData;
}

export function PublicQuestionListItem({
  question,
}: PublicQuestionListItemProps): React.ReactElement {
  const status = resolveQuestionStatusPresentation(question.status);
  const replyLabel = `${question.replyCount} ${question.replyCount === 1 ? 'reply' : 'replies'}`;
  const correctionLabel = question.editoriallyCorrected
    ? `. ${EDITORIAL_CORRECTION_PRESENTATIONS.CUSTOMER_OR_GUEST}`
    : '';

  return (
    <li className="fmp-browse-item fmp-browse-item--clickable">
      <a
        className="fmp-browse-item__link"
        href={canonicalQuestionHref(question.slug)}
        aria-label={`${question.title}. ${status.customerLabel}. Asked by ${question.posterName}. ${replyLabel}${correctionLabel}.`}
      >
        <span className="fmp-browse-item__media" aria-hidden="true">
          {question.thumbnailUrl ? (
            <img
              className="fmp-browse-item__thumbnail"
              src={question.thumbnailUrl}
              alt=""
              width="96"
              height="96"
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="fmp-browse-item__placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 7.5A1.5 1.5 0 0 1 5.5 6h13A1.5 1.5 0 0 1 20 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 16.5z" />
                <path d="m4 15 4.5-4.5 3.5 3.5 2-2 6 5" />
                <circle cx="15.5" cy="9.5" r="1.5" />
              </svg>
            </span>
          )}
        </span>

        <span className="fmp-browse-item__content">
          <span className="fmp-browse-item__header">
            <span className="fmp-browse-item__title">{question.title}</span>
            <span className={`fmp-question-status-badge fmp-question-status-badge--${status.tone}`}>
              {question.hasSolution && (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  aria-hidden="true"
                  className="fmp-question-status-badge__icon"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
              {status.customerLabel}
            </span>
          </span>
          {question.description && (
            <span className="fmp-browse-item__description">{question.description}</span>
          )}
          <span className="fmp-browse-item__meta">
            <span className="fmp-browse-item__author">{question.posterName}</span>
            <span className="fmp-browse-item__separator" aria-hidden="true">&middot;</span>
            <time dateTime={new Date(question.createdAt).toISOString()}>
              {formatTimeAgo(question.createdAt)}
            </time>
            <span className="fmp-browse-item__separator" aria-hidden="true">&middot;</span>
            <span className="fmp-browse-item__replies">{replyLabel}</span>
            {question.editoriallyCorrected && (
              <>
                <span className="fmp-browse-item__separator" aria-hidden="true">&middot;</span>
                <span>{EDITORIAL_CORRECTION_PRESENTATIONS.CUSTOMER_OR_GUEST}</span>
              </>
            )}
          </span>
        </span>
      </a>
    </li>
  );
}
